"use client";

import Image from "next/image";
import { HrtSelector } from "./HrtSelector";
import { LearnMoreButton } from "./LearnMoreButton";
import { ProductProvider } from "./ProductContext";
import { ProductDetails } from "./ProductDetails";
import { ProductName } from "./ProductName";

import {
  type HrtDescriptionEntry,
  type ProductWithTranslations,
  getTranslatedLearnMoreButtonText,
  getTranslatedProductName,
} from "@/app/directories/shop/helper";
import {
  useInsuranceConnectorSend,
  useInsuranceConnectorState,
} from "@frontend/components/InsuranceConnector/insurance-connector-provider";
import type { StorefrontProductPageHrtType } from "@frontend/components/InsuranceStepper/InsuranceStepper.type";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { useEffect, useRef } from "react";

interface ProductDisplayProps {
  product: {
    name: string;
    displayMetadata?: {
      imageUrl: string;
      preDiscount: string;
      learnMoreButtonText?: string;
    };
    ProductTranslation?: ProductWithTranslations["ProductTranslation"];
    productToHrt: Array<{
      hrtId: number;
      product_component_type: "DEFAULT" | "INITIAL_REQUIRED";
      hrt: StorefrontProductPageHrtType;
    }>;
  };
  clientSlug: string;
  orgId: number;
  /** Whether the page renders sections below the display to scroll to. */
  hasSectionsBelow?: boolean;
}

export function ProductDisplay({
  product,
  clientSlug,
  orgId,
  hasSectionsBelow = false,
}: ProductDisplayProps) {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const queryClient = useQueryClient();
  const configFlags =
    queryClient.getQueryData<Record<string, boolean>>(["configFlags", orgId]) ??
    {};
  const eligibilityCheckResult = useBoundStore.use.eligibilityCheckResult();
  const sendInsurance = useInsuranceConnectorSend();
  const insuranceState = useInsuranceConnectorState();
  const hasAutoOpened = useRef(false);

  // Auto-open the insurance connector when the consumer hasn't been
  // screened yet on orgs that use the eligibility system. The connector
  // handles the DOB/state eligibility flow itself (replacing the old
  // standalone EligibilityModal).
  useEffect(() => {
    if (hasAutoOpened.current) return;
    if (!configFlags?.storefront_offboard_consumers) return;
    if (eligibilityCheckResult.result !== "not_checked") return;
    if (insuranceState.context.uiMode !== "closed") return;
    hasAutoOpened.current = true;
    sendInsurance({ type: "OPEN" });
  }, [
    configFlags?.storefront_offboard_consumers,
    eligibilityCheckResult.result,
    insuranceState.context.uiMode,
    sendInsurance,
  ]);

  const translatedProductName = getTranslatedProductName(
    product as ProductWithTranslations,
    languageCode,
  );
  const learnMoreButtonText = hasSectionsBelow
    ? getTranslatedLearnMoreButtonText(product, languageCode)
    : undefined;

  // Same query key as HrtSelector, so this reads the shared cache entry.
  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  return (
    <div className="flex lg:flex-row flex-col w-full lg:h-[calc(100vh-72px)] sm:h-full min-h-fit bg-white">
      <Image
        src={product.displayMetadata?.imageUrl ?? ""}
        alt={product.name}
        width={1200}
        height={1200}
        className="w-full lg:w-3/5 object-cover sm:max-h-[40vh] lg:max-h-[calc(100vh)]"
      />
      <ProductProvider
        initialOptions={product.productToHrt.map((option) => ({
          product_component_type: option.product_component_type,
          hrt: {
            hrtId: option.hrt.hrtId,
            name: option.hrt.displayName ?? option.hrt.name,
            cash_prices: option.hrt.cash_prices,
            displayMetadata: {
              description: option.hrt.displayMetadata?.description ?? [],
              ingredients: option.hrt.displayMetadata?.ingredients ?? [],
            },
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            hrtPaymentType: option.hrt.hrtPaymentType as any,
            isConsumerFacing: option.hrt.isConsumerFacing,
            ehrPrimaryAppointmentTypeId: option.hrt
              .ehrPrimaryAppointmentTypeId as string[],
            isBookableOnStorefront: option.hrt.isBookableOnStorefront,
            popularity: option.hrt.popularity,
            HrtTranslation: (option.hrt.HrtTranslation ?? []) as Array<{
              languageCode: string;
              name?: string;
              displayName?: string;
              displayMetadata?: {
                description?: Array<HrtDescriptionEntry>;
                ingredients?: Array<{ value: string }>;
              };
            }>,
          },
        }))}
      >
        <div className="flex flex-col w-full lg:w-2/5 lg:px-12 sm:px-4 lg:py-8 lg:pb-12 sm:pb-4 sm:py-4 gap-4 justify-center min-h-fit ">
          <div className="flex flex-col gap-4">
            {/* <PriceDisplay /> */}
            <ProductName productName={translatedProductName} />
            <ProductDetails />
          </div>
          <HrtSelector
            clientSlug={clientSlug}
            orgId={orgId}
            product={product}
          />
          <LearnMoreButton
            label={learnMoreButtonText}
            primaryColor={organizationAssets?.primaryColor ?? undefined}
          />
        </div>
      </ProductProvider>
    </div>
  );
}
